import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiModuleExhibitionist } from '@wix/da-papi-types';
import useModuleData from '@wix/da-gruser-shared/pkg/hooks/useModuleData';
import BuyButton from '@wix/da-ds/pkg/Buttons/BuyButton';
import LazyContext from '../../../LazyLoadingImage/LazyContext';
import StandardThumb from '../../../DeviationViews/Thumb/Standard';
import { AllowedIndicators } from '../../../DeviationViews/Thumb/legos/DeviationThumbIndicators';
import AdoptableShopCard from '../../../ShopCard/AdoptableShopCard';
import Slider, { SeeAllSlide } from '@wix/da-ds/pkg/Slider';
import { useCurrentUser } from '../../../utils/hooks/useCurrentUser';
import { ExhibitionistWidgetType, GallectionSourceType } from '../types';
import { useExhibitionistType } from '../hooks';

import s from './ExhibitionistStrip.scss';

export interface Props {
  moduleId: number;
}

const ExhibitionistStrip: React.FC<Props> = ({ moduleId }) => {
  const { t } = useTranslation();
  const allowedIndicators = AllowedIndicators.Discount;
  const exhibitionistType = useExhibitionistType();
  const currentUser = useCurrentUser();
  const shortStrip = exhibitionistType === ExhibitionistWidgetType.STRIP_SHORT;
  const { moduleData } = useModuleData<PapiModuleExhibitionist>(moduleId);
  const { deviations, seeAllUrl, sourceType, hasMore } = moduleData;
  const isAdoptables =
    sourceType === GallectionSourceType.ADOPTABLE_FOR_SALE ||
    sourceType === GallectionSourceType.ADOPTABLE_BOUGHT;

  return (
    <LazyContext.Provider value={true}>
      <Slider
        className={c(
          s['slider'],
          isAdoptables && s['slider-adoptables'],
          shortStrip && s['slider-short']
        )}
        scrollBy={0.5}
      >
        {deviations.map(deviation => {
          if (isAdoptables) {
            const extraProps = {};

            const adoptable = deviation.adoptable;
            const isOpenForSale = adoptable?.thumbSaleState === 'open';
            extraProps['hidePrice'] = !isOpenForSale;
            extraProps['hideSold'] = true;
            if (
              !isOpenForSale &&
              adoptable?.owner?.userId === currentUser?.userId
            ) {
              extraProps['detailsRightSlot'] = (
                <BuyButton
                  isFakeButton
                  size="medium"
                  fillContainerWidth
                  rounded
                >
                  {t('common.sell')}
                </BuyButton>
              );
            }

            return (
              <AdoptableShopCard
                key={deviation.deviationId}
                deviation={deviation}
                width={328}
                allowedIndicators={allowedIndicators}
                {...extraProps}
              />
            );
          }
          return (
            <StandardThumb
              key={deviation.deviationId}
              className={s['slider-item']}
              deviation={deviation}
              height={shortStrip ? 180 : 240}
              responsive={false}
            />
          );
        })}
        {hasMore && seeAllUrl && <SeeAllSlide seeAllUrl={seeAllUrl} />}
      </Slider>
    </LazyContext.Provider>
  );
};
ExhibitionistStrip.displayName = 'ExhibitionistStrip';

export default ExhibitionistStrip;
